import { fetchAuthSession, updateUserAttribute, fetchUserAttributes } from '@aws-amplify/auth';

// Simplified User interface - Cognito-only model
export interface User {
  id: string; // cognito sub
  email: string;
  name: string;
  organization_name: string;
  profile_image_url?: string;
}

// Simple user cache
let cache: {
  user: User | null;
} = {
  user: null
};

class UserService {
  /**
   * Get auth token from Cognito
   */
  async getAuthToken(): Promise<string> {
    const session = await fetchAuthSession();
    const jwtToken = session.tokens?.idToken?.toString();
    
    if (!jwtToken) {
      throw new Error('No authentication token available');
    }
    
    return jwtToken;
  }

  /**
   * Get current user from Cognito directly
   */
  async getCurrentUser(): Promise<User | null> {
    if (cache.user) {
      return cache.user;
    }

    try {
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken || !attributes.email) {
        return null;
      }

      const user: User = {
        id: idToken.payload.sub as string,
        email: attributes.email,
        name: attributes.name || attributes.email.split('@')[0],
        organization_name: attributes['custom:organization_name'] || '',
        profile_image_url: attributes['custom:profile_image_url'] || undefined,
      };
      
      cache.user = user;
      return cache.user;
    } catch (error: any) {
      console.error('[UserService] Error fetching current user:', error);
      return null;
    }
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    cache.user = null;
  }

  /**
   * Refresh user data from Cognito
   */
  async refreshUserData(): Promise<User | null> {
    this.clearCache();
    return this.getCurrentUser();
  }

  /**
   * Check if user setup is complete
   */
  async checkUserSetupStatus(): Promise<{
    isSetupComplete: boolean;
    hasOrganization: boolean;
    user: User | null;
  }> {
    try {
      const user = await this.getCurrentUser();
      const hasOrganization = Boolean(user?.organization_name);
      
      return {
        isSetupComplete: Boolean(user && hasOrganization),
        hasOrganization,
        user
      };
      
    } catch (error: any) {
      console.error('[UserService] Error checking user setup status:', error);
      return {
        isSetupComplete: false,
        hasOrganization: false,
        user: null
      };
    }
  }

  /**
   * Set up organization name (simplified)
   */
  async setupOrganization(organizationName: string): Promise<User> {
    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'custom:organization_name',
        value: organizationName
      }
    });
    
    this.clearCache();
    const user = await this.getCurrentUser();
    
    if (!user) {
      throw new Error('Failed to retrieve updated user data');
    }
    
    return user;
  }

  /**
   * Update organization name
   */
  async updateOrganization(organizationName: string): Promise<User> {
    if (!organizationName) {
      throw new Error('Organization name is required');
    }
    
    return this.setupOrganization(organizationName);
  }

  /**
   * Update user profile image URL
   */
  async updateProfileImage(imageUrl: string): Promise<User> {
    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'custom:profile_image_url',
        value: imageUrl
      }
    });
    
    this.clearCache();
    const user = await this.getCurrentUser();
    
    if (!user) {
      throw new Error('Failed to retrieve updated user data');
    }
    
    return user;
  }
}

// Export singleton instance
export const userService = new UserService(); 